import React from 'react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { trackingEvents } from 'modules/core/lib/Tracking'
import users from 'modules/core/store/collections/users'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ReportsSqlEditor from 'modules/reports/components/SqlEditor'
import type { RouteContextParams } from 'modules/reports/lib/RouterContextParams'

import NotFound from '../components/NotFound'
import SaveModal, { SAVE_ACTION } from '../components/SaveReportModal'
import useReport from '../hooks/useReport'
import { useReportTracking } from '../hooks/useReportTracking'
import useStore from '../hooks/useStore'
import ReportStruct from '../lib/ReportStruct'
import { PromptProvider } from '../lib/promptContext'

const OpenSqlReportPage = () => {
  const params = useParams()
  const [search, _] = useSearchParams()
  const { loading } = useOutletContext<RouteContextParams>()

  const { loading: isFeatureLoading, featureAvailability } = useFeatureAvailability(
    Features.analytics_advanced_reports
  )

  const [sql, setSql] = React.useState(null)

  const { report, isNotFound } = useReport({
    id: Number(params.id),
    loading,
  })

  async function fetchSql() {
    if (!report) {
      return
    }

    const reportStruct = ReportStruct.buildFromReport(report)
    const sql = await reportStruct.toSql([])
    setSql(sql)
  }

  const { loading: sqlLoading } = useUnsafeAsyncData(fetchSql, [report])

  const store = useStore({
    report,
    origin: report?.origin && report.origin == 'template' ? 'modified_template' : undefined,
  })

  const { track } = useReportTracking(store)

  useUnsafeEffect(() => {
    if (!report) return

    track(trackingEvents.reports.reportsSqlEditVisited)
    track(trackingEvents.reports.openCustomerReport)
  }, [track, report])

  const isAuthorized =
    featureAvailability.available && users.current.policy.can('reports.useReports') // nosemgrep

  const showSaveModal = search.get('action') == SAVE_ACTION

  return (
    <ResponsivePage.Body
      loading={loading || isFeatureLoading || sqlLoading}
      authorized={isAuthorized}
      error={undefined}
      variant="full"
      showHeader={false}
    >
      <NotFound notFound={isNotFound}>
        <PromptProvider>
          <Box flexDirection="column">
            {showSaveModal && report && store && <SaveModal report={report} store={store} />}
            {store && <ReportsSqlEditor store={store} sql={sql} />}
          </Box>
        </PromptProvider>
      </NotFound>
    </ResponsivePage.Body>
  )
}

export default OpenSqlReportPage
